<script lang="ts">
  import { onMount } from 'svelte';
  import { onAuthStateChanged } from 'firebase/auth';

  import Background from './components/Background.svelte';
  import { auth } from './firebase';
  import Calendar from './components/Calendar.svelte';
  import Login from './components/Login.svelte';
  import Loading from './components/Loading.svelte';
  import { signIn } from './firebase';
  import {
    loading,
    user as userStore,
    resetLoadingState,
  } from './stores/user';
  import {
    currentDate,
    currentMonth,
    year,
    month,
    prevMonthInfo,
    color,
  } from './stores/appData';
  import { getHSLArray, months } from './util';

  $effect(() => {
    if ($currentDate) currentMonth.set($currentDate.getMonth());
  });
  $effect(() => {
    // Ensure $currentMonth is defined before trying to access months[$currentMonth]
    if ($currentDate && $currentMonth !== undefined) month.set(months[$currentMonth]);
  });
  $effect(() => {
    if ($currentDate) year.set($currentDate.getFullYear());
  });

  let body: HTMLBodyElement | null = null;
  onMount(() => {
    body = document.querySelector('body');

    // Force loading to false after a certain timeout as a fallback
    setTimeout(() => {
      if ($loading) {
        resetLoadingState();
      }
    }, 5000);
  });

  const DEFAULT_COLOR = '#000000';

  function updateColor(element: HTMLElement): void {
    const value = getComputedStyle(element).getPropertyValue('--altColor').trim();
    const result = getHSLArray(value);
    color.set(result || DEFAULT_COLOR);
  }

  $effect(() => {
    if ($currentDate && body) {
      body.classList.remove(...body.classList);
      body.classList.add($month.toLocaleLowerCase());
      setTimeout(() => {
        if (body) {
          updateColor(body);
        }
      }, 100);
    }
  });

  interface AppUser {
    uid: string;
    displayName: string | null;
  }

  let user: AppUser | null = $state(null);
  let authInitialized = $state(false);

  onMount(() => {
    const unsubscribe = onAuthStateChanged(auth, (newUser: any) => {
      authInitialized = true;

      if (newUser?.uid) {
        user = {
          uid: newUser.uid,
          displayName: newUser.displayName ?? null,
        };
      } else {
        user = null;
      }
    });
    return unsubscribe;
  });

  let calendarId: string | undefined = $state(undefined);

  $effect(() => {
    if ($userStore) {
      if ($userStore.sharedId && $userStore.verified) {
        calendarId = $userStore.sharedId;
      } else if (user?.uid) {
        calendarId = user.uid;
      }
    }
  });

  onMount(() => {
    currentDate.set(new Date());
  });
</script>

<Background animated={!user} />

<svelte:head>
  <meta name="theme-color" content={$color || DEFAULT_COLOR} />

  {#if user?.displayName}
    <title>{user.displayName}'s Menu</title>
  {:else}
    <title>Login to Menu Planner</title>
  {/if}
</svelte:head>

{#if !authInitialized || $loading}
  <Loading />
{:else if user?.uid}
  <Calendar uid={calendarId || user.uid} />
{:else}
  <Login on:click={signIn} />
{/if}
